import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import { ENTITY_TYPES, DEFAULT_TAX_RATES } from '../../../services/investmentPlanningService';
import {
  newShareholderId,
  normalizeDividendPolicy,
  normalizeCorpTaxAccounts,
  validateShareholderPercents,
  DEFAULT_DIVIDEND_ORDER,
} from '../../../services/planningTax/corporateIntegration';

const EMPTY_SHAREHOLDER = {
  id: '',
  personEntityId: '',
  name: '',
  ownershipPercent: '',
  marginalTaxRate: '',
};

const PROVINCE_OPTIONS = [
  { value: 'QC', label: 'Québec (plafond intérêts / placements)' },
  { value: 'ON', label: 'Ontario' },
  { value: 'BC', label: 'Colombie-Britannique' },
  { value: 'AB', label: 'Alberta' },
  { value: 'OTHER', label: 'Autre province / territoire' },
];

const EMPTY_FORM = {
  name: '',
  type: 'personnel',
  description: '',
  province: 'QC',
  marginalTaxRate: DEFAULT_TAX_RATES.personnel,
  shareholders: [],
  corpTaxAccounts: { cdcBalance: '', imrtdBalance: '', gripBalance: '' },
  dividendPolicy: { autoDistributePercent: '0', defaultOrder: [...DEFAULT_DIVIDEND_ORDER], gripAccrualPercent: '0' },
};

const EntitiesManager = ({ entities = [], onAdd, onUpdate, onDelete }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [shareholderError, setShareholderError] = useState('');

  const personnelOptions = entities
    .filter((e) => e.type === 'personnel' && e.id !== editingId)
    .map((e) => ({ value: e.id, label: e.name }));

  const buildPayload = () => {
    const payload = {
      name: form.name.trim(),
      type: form.type,
      description: form.description || '',
      province: form.province || 'QC',
      marginalTaxRate: parseFloat(form.marginalTaxRate) || DEFAULT_TAX_RATES[form.type],
    };
    if (form.type === 'corporation') {
      payload.shareholders = (form.shareholders || []).map((sh) => ({
        id: sh.id || newShareholderId(),
        personEntityId: sh.personEntityId || null,
        name: sh.name || '',
        ownershipPercent: parseFloat(sh.ownershipPercent) || 0,
        marginalTaxRate: sh.marginalTaxRate !== '' && sh.marginalTaxRate != null
          ? parseFloat(sh.marginalTaxRate)
          : undefined,
      }));
      payload.corpTaxAccounts = {
        cdcBalance: parseFloat(form.corpTaxAccounts?.cdcBalance) || 0,
        imrtdBalance: parseFloat(form.corpTaxAccounts?.imrtdBalance) || 0,
        gripBalance: parseFloat(form.corpTaxAccounts?.gripBalance) || 0,
      };
      payload.dividendPolicy = {
        autoDistributePercent: parseFloat(form.dividendPolicy?.autoDistributePercent) || 0,
        defaultOrder: form.dividendPolicy?.defaultOrder || [...DEFAULT_DIVIDEND_ORDER],
        gripAccrualPercent: parseFloat(form.dividendPolicy?.gripAccrualPercent) || 0,
      };
    }
    return payload;
  };

  const handleSave = () => {
    if (!form.name.trim()) return;
    if (form.type === 'corporation') {
      const v = validateShareholderPercents(form.shareholders);
      if (form.shareholders?.length > 0 && !v.valid) {
        setShareholderError(`Les parts doivent totaliser 100 % (actuel: ${v.sum.toFixed(1)} %)`);
        return;
      }
      for (const sh of form.shareholders || []) {
        if (!sh.personEntityId && !sh.name?.trim()) {
          setShareholderError('Chaque actionnaire doit être lié à un Personnel ou avoir un nom.');
          return;
        }
        if (!sh.personEntityId && !sh.marginalTaxRate) {
          setShareholderError('Taux marginal requis pour un actionnaire sans lien Personnel.');
          return;
        }
      }
    }
    setShareholderError('');
    const payload = buildPayload();
    if (editingId) onUpdate(editingId, payload);
    else onAdd(payload);
    setForm(EMPTY_FORM);
    setShowForm(false);
    setEditingId(null);
  };

  const handleEdit = (entity) => {
    const policy = normalizeDividendPolicy(entity) || { autoDistributePercent: 0, defaultOrder: [...DEFAULT_DIVIDEND_ORDER], gripAccrualPercent: 0 };
    const accounts = normalizeCorpTaxAccounts(entity);
    setForm({
      name: entity.name,
      type: entity.type,
      description: entity.description || '',
      province: entity.province || 'QC',
      marginalTaxRate: entity.marginalTaxRate ?? DEFAULT_TAX_RATES[entity.type] ?? 50,
      shareholders: (entity.shareholders || []).map((sh) => ({
        id: sh.id || newShareholderId(),
        personEntityId: sh.personEntityId || '',
        name: sh.name || '',
        ownershipPercent: String(sh.ownershipPercent ?? ''),
        marginalTaxRate: sh.marginalTaxRate != null ? String(sh.marginalTaxRate) : '',
      })),
      corpTaxAccounts: {
        cdcBalance: String(accounts.cdcBalance || ''),
        imrtdBalance: String(accounts.imrtdBalance || ''),
        gripBalance: String(accounts.gripBalance || ''),
      },
      dividendPolicy: {
        autoDistributePercent: String(policy.autoDistributePercent ?? 0),
        defaultOrder: policy.defaultOrder,
        gripAccrualPercent: String(policy.gripAccrualPercent ?? 0),
      },
    });
    setEditingId(entity.id);
    setShowForm(true);
    setShareholderError('');
  };

  const handleCancel = () => {
    setForm(EMPTY_FORM);
    setShowForm(false);
    setEditingId(null);
    setShareholderError('');
  };

  const addShareholder = () => {
    setForm({
      ...form,
      shareholders: [...(form.shareholders || []), { ...EMPTY_SHAREHOLDER, id: newShareholderId() }],
    });
  };

  const updateShareholder = (idx, patch) => {
    const next = [...(form.shareholders || [])];
    next[idx] = { ...next[idx], ...patch };
    setForm({ ...form, shareholders: next });
  };

  const removeShareholder = (idx) => {
    setForm({ ...form, shareholders: (form.shareholders || []).filter((_, i) => i !== idx) });
  };

  const ownershipSum = (form.shareholders || []).reduce((s, sh) => s + (parseFloat(sh.ownershipPercent) || 0), 0);

  return (
    <div className="bg-card border border-border rounded-xl p-5 md:p-6" data-testid="entities-manager">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-foreground flex items-center gap-2" style={{ fontFamily: 'var(--font-heading)' }}>
          <Icon name="Users" size={20} className="text-primary" />
          Détenteurs
        </h2>
        <Button size="sm" iconName="Plus" iconPosition="left" onClick={() => { setForm(EMPTY_FORM); setEditingId(null); setShowForm(true); }} data-testid="add-entity-btn">
          Ajouter
        </Button>
      </div>

      {showForm && (
        <div className="bg-muted rounded-lg p-4 mb-4 space-y-3" data-testid="entity-form">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <Input label="Nom" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex: Gestion ABC Inc." required data-testid="entity-name-input" />
            <Select label="Type" value={form.type} onChange={(v) => setForm({
              ...form,
              type: v,
              marginalTaxRate: DEFAULT_TAX_RATES[v] ?? form.marginalTaxRate,
              shareholders: v === 'corporation' ? (form.shareholders?.length ? form.shareholders : []) : [],
            })} options={ENTITY_TYPES} data-testid="entity-type-select" />
            <Select
              label="Province (fiscalité)"
              value={form.province || 'QC'}
              onChange={(v) => setForm({ ...form, province: v })}
              options={PROVINCE_OPTIONS}
              data-testid="entity-province-select"
            />
            <Input label="Taux marginal d'imposition (%)" type="number" value={form.marginalTaxRate}
              onChange={(e) => setForm({ ...form, marginalTaxRate: e.target.value })}
              placeholder={form.type === 'personnel' ? '53.31' : '26.50'} data-testid="entity-tax-rate-input" />
          </div>
          <Input label="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Optionnel" data-testid="entity-desc-input" />

          {form.type === 'corporation' && (
            <div className="space-y-4 border border-primary/20 rounded-lg p-4 bg-primary/5" data-testid="corp-shareholders-section">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-foreground">Actionnaires</p>
                <Button size="xs" variant="outline" iconName="Plus" onClick={addShareholder} data-testid="add-shareholder-btn">Ajouter</Button>
              </div>
              {(form.shareholders || []).length === 0 && (
                <p className="text-xs text-muted-foreground">Ajoutez les actionnaires et leurs parts (total 100 %).</p>
              )}
              {(form.shareholders || []).map((sh, idx) => (
                <div key={sh.id || idx} className="grid grid-cols-1 md:grid-cols-5 gap-2 items-end border border-border rounded-md p-2 bg-card">
                  <Select
                    label="Lien Personnel"
                    value={sh.personEntityId}
                    onChange={(v) => updateShareholder(idx, { personEntityId: v, name: v ? '' : sh.name })}
                    options={[{ value: '', label: 'Profil inline' }, ...personnelOptions]}
                    data-testid={`shareholder-person-${idx}`}
                  />
                  {!sh.personEntityId && (
                    <Input label="Nom" value={sh.name} onChange={(e) => updateShareholder(idx, { name: e.target.value })} placeholder="Nom actionnaire" />
                  )}
                  <Input label="Parts %" type="number" value={sh.ownershipPercent} onChange={(e) => updateShareholder(idx, { ownershipPercent: e.target.value })} data-testid={`shareholder-pct-${idx}`} />
                  <Input label="Taux marginal %" type="number" value={sh.marginalTaxRate} onChange={(e) => updateShareholder(idx, { marginalTaxRate: e.target.value })}
                    placeholder={sh.personEntityId ? 'Auto' : '53.31'} disabled={!!sh.personEntityId && !sh.marginalTaxRate} />
                  <Button size="xs" variant="ghost" iconName="Trash2" onClick={() => removeShareholder(idx)} style={{ color: 'var(--color-destructive)' }} />
                </div>
              ))}
              {(form.shareholders || []).length > 0 && (
                <p className={`text-xs ${Math.abs(ownershipSum - 100) <= 0.01 ? 'text-muted-foreground' : 'text-destructive'}`}>
                  Total des parts : {ownershipSum.toFixed(1)} %
                </p>
              )}
              {shareholderError && <p className="text-xs text-destructive">{shareholderError}</p>}

              <p className="text-sm font-semibold text-foreground pt-2">Comptes fiscaux (soldes d&apos;ouverture)</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Input label="CDC ($)" type="number" value={form.corpTaxAccounts.cdcBalance} onChange={(e) => setForm({ ...form, corpTaxAccounts: { ...form.corpTaxAccounts, cdcBalance: e.target.value } })} data-testid="corp-cdc-balance" />
                <Input label="IMRTD ($)" type="number" value={form.corpTaxAccounts.imrtdBalance} onChange={(e) => setForm({ ...form, corpTaxAccounts: { ...form.corpTaxAccounts, imrtdBalance: e.target.value } })} data-testid="corp-imrtd-balance" />
                <Input label="GRIP ($)" type="number" value={form.corpTaxAccounts.gripBalance} onChange={(e) => setForm({ ...form, corpTaxAccounts: { ...form.corpTaxAccounts, gripBalance: e.target.value } })} data-testid="corp-grip-balance" />
              </div>

              <p className="text-sm font-semibold text-foreground pt-2">Politique de dividendes</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Input label="Distribution auto du cashflow op. (%)" type="number" value={form.dividendPolicy.autoDistributePercent}
                  onChange={(e) => setForm({ ...form, dividendPolicy: { ...form.dividendPolicy, autoDistributePercent: e.target.value } })}
                  placeholder="0" data-testid="entity-dividend-rate-input" />
                <Input label="Crédit GRIP annuel (% revenu actif)" type="number" value={form.dividendPolicy.gripAccrualPercent}
                  onChange={(e) => setForm({ ...form, dividendPolicy: { ...form.dividendPolicy, gripAccrualPercent: e.target.value } })} placeholder="0" />
              </div>
              <p className="text-xs text-muted-foreground">Ordre de distribution : capital (CDC) → admissible (GRIP) → ordinaire (IMRTD).</p>
            </div>
          )}

          <div className="flex gap-2">
            <Button size="sm" onClick={handleSave} data-testid="entity-save-btn">{editingId ? 'Mettre à jour' : 'Ajouter'}</Button>
            <Button size="sm" variant="outline" onClick={handleCancel} data-testid="entity-cancel-btn">Annuler</Button>
          </div>
        </div>
      )}

      {entities.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-4">Aucun détenteur configuré.</p>
      ) : (
        <div className="space-y-2">
          {entities.map((entity) => {
            const policy = entity.type === 'corporation' ? normalizeDividendPolicy(entity) : null;
            const shCount = entity.shareholders?.length || 0;
            return (
              <div key={entity.id} className="flex items-center justify-between p-3 rounded-lg border border-border hover:bg-muted/50 transition-colors" data-testid={`entity-row-${entity.id}`}>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-md flex items-center justify-center" style={{ background: entity.type === 'personnel' ? 'rgba(59,130,246,0.1)' : 'rgba(193,123,71,0.1)' }}>
                    <Icon name={entity.type === 'personnel' ? 'User' : 'Building2'} size={16} style={{ color: entity.type === 'personnel' ? 'var(--color-primary)' : 'var(--color-accent)' }} />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">{entity.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {ENTITY_TYPES.find((t) => t.value === entity.type)?.label || entity.type}
                      {entity.marginalTaxRate != null && (
                        <span className="ml-2" style={{ fontFamily: 'var(--font-data)' }}>
                          — Taux: {parseFloat(entity.marginalTaxRate).toFixed(1)}%
                        </span>
                      )}
                      {policy && (
                        <span className="ml-2" style={{ fontFamily: 'var(--font-data)' }}>
                          | {shCount} actionnaire{shCount !== 1 ? 's' : ''} · Dist. {policy.autoDistributePercent}%
                        </span>
                      )}
                    </p>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button size="xs" variant="ghost" iconName="Pencil" onClick={() => handleEdit(entity)} data-testid={`edit-entity-${entity.id}`} />
                  <Button size="xs" variant="ghost" iconName="Trash2" onClick={() => onDelete(entity.id)} data-testid={`delete-entity-${entity.id}`} style={{ color: 'var(--color-destructive)' }} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default EntitiesManager;
